import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import type { ClassTrial } from '@/hooks/useClassTrials';
import { getTrialStatusColor } from './classTrialStatusColors';

interface ClassTrialTableProps {
  trials: ClassTrial[];
  isLoading: boolean;
  onEdit: (trial: ClassTrial) => void;
  onDelete: (id: string) => void;
}

export function ClassTrialTable({ trials, isLoading, onEdit, onDelete }: ClassTrialTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (trials.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        ไม่พบข้อมูลทดลองเรียนในช่วงเวลานี้
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>วันที่</TableHead>
            <TableHead>ครู</TableHead>
            <TableHead>นักเรียน</TableHead>
            <TableHead>ชั้น</TableHead>
            <TableHead>ประเภท</TableHead>
            <TableHead>สถานที่</TableHead>
            <TableHead>สถานะ</TableHead>
            <TableHead className="text-center">ชม.</TableHead>
            <TableHead className="text-right">ค่าใช้จ่าย</TableHead>
            <TableHead className="text-right">จัดการ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {trials.map((trial) => (
            <TableRow key={trial.id}>
              <TableCell className="whitespace-nowrap">
                {trial.class_date ? new Date(trial.class_date).toLocaleDateString('th-TH') : '-'}
              </TableCell>
              <TableCell>{trial.teacher_code || '-'}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{trial.student_nickname || '-'}</span>
                  {trial.student_name && (
                    <span className="text-xs text-muted-foreground">{trial.student_name}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>{trial.grade || '-'}</TableCell>
              <TableCell>{trial.class_type || '-'}</TableCell>
              <TableCell>
                {trial.location === 'online' ? 'ออนไลน์' : trial.location === 'onsite' ? 'ออนไซต์' : trial.location || '-'}
              </TableCell>
              <TableCell>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTrialStatusColor(trial.status || '')}`}>
                  {trial.status || '-'}
                </span>
              </TableCell>
              <TableCell className="text-center">{(trial as any).hours ?? 1}</TableCell>
              <TableCell className="text-right font-medium">{trial.cost} ฿</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(trial)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(trial.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
